import { useState } from "react";
import { CheckCircle2, Trash2, Loader2, Clock, Banknote, FileText, Receipt, CreditCard } from "lucide-react";

const SOURCE_META = {
  bank_line: { label: "BANQUE", color: "#38bdf8", icon: Banknote },
  expense_payment: { label: "PAIEMENT DÉPENSE", color: "#38bdf8", icon: Banknote },
  invoice: { label: "FACTURE", color: "#22c55e", icon: FileText },
  expense: { label: "DÉPENSE", color: "#f97316", icon: Receipt },
  payment: { label: "ENCAISSEMENT", color: "#a855f7", icon: CreditCard },
};

const isContra = (n) => !/^(512|445|58|53)/.test(n);
const selectCls = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs focus:border-[#f7c935] outline-none max-w-xs";

export function ReviewCard({ entry, accounts, onApprove, onReject }) {
  const meta = SOURCE_META[entry.source_type] || SOURCE_META.bank_line;
  const Icon = meta.icon;
  const contra = (entry.lines || []).find((l) => isContra(l.account_number));
  const [account, setAccount] = useState(contra?.account_number || "");
  const [busy, setBusy] = useState(null);
  const changed = contra && account && account !== contra.account_number;
  const amount = entry.bank_line?.amount ?? entry.total_debit;

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } finally { setBusy(null); }
  };

  return (
    <div className={`bg-[#0b0811] border rounded-xl p-4 transition-colors ${entry.overdue ? "border-red-500/40" : "border-white/5 hover:border-white/10"}`} data-testid={`review-card-${entry.entry_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[9px] px-1.5 py-0.5 rounded font-black tracking-wider flex items-center gap-1" style={{ background: `${meta.color}20`, color: meta.color }}>
              <Icon className="w-3 h-3" /> {meta.label}
            </span>
            <span className="text-xs text-slate-500 font-mono">{entry.date}</span>
            {entry.piece_ref && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded font-mono text-slate-400">{entry.piece_ref}</span>}
            {entry.overdue && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-black bg-red-500/15 text-red-400 flex items-center gap-1" data-testid={`review-overdue-${entry.entry_id}`}>
                <Clock className="w-3 h-3" /> SANS JUSTIFICATIF
              </span>
            )}
          </div>
          <div className="text-sm text-white font-semibold truncate" data-testid={`review-label-${entry.entry_id}`}>{entry.label}</div>
          {entry.bank_line && entry.bank_line.label !== entry.label && (
            <div className="text-[11px] text-slate-500 truncate">Libellé banque : {entry.bank_line.label}</div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(entry.review_reasons || []).map((r) => (
              <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300" data-testid="review-reason">{r}</span>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-black font-mono" style={{ color: amount >= 0 ? "#22c55e" : "#ef4444" }} data-testid={`review-amount-${entry.entry_id}`}>
            {amount >= 0 ? "+" : ""}{Number(amount || 0).toFixed(2)} €
          </div>
          <div className="text-[10px] text-slate-500 uppercase">{entry.journal_code} · {entry.lines?.length} lignes</div>
        </div>
      </div>

      <table className="w-full text-[11px] mt-3">
        <tbody>
          {(entry.lines || []).map((l) => (
            <tr key={l.line_id} className="border-t border-white/5">
              <td className="px-2 py-1 font-mono text-slate-300 w-20">{isContra(l.account_number) && changed ? <span className="text-[#f7c935]">{account}</span> : l.account_number}</td>
              <td className="px-2 py-1 text-slate-400 truncate">{l.label || l.account_label}</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300 w-24">{l.debit ? l.debit.toFixed(2) : ""}</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300 w-24">{l.credit ? l.credit.toFixed(2) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {contra && (
          <select value={account} onChange={(e) => setAccount(e.target.value)} className={selectCls} data-testid={`review-account-select-${entry.entry_id}`}>
            {accounts.map((a) => <option key={a.account_number} value={a.account_number}>{`${a.account_number} — ${a.label}`}</option>)}
          </select>
        )}
        <div className="flex-1" />
        <button
          onClick={() => run("reject", () => onReject(entry))}
          disabled={!!busy}
          className="text-red-400 hover:text-red-300 text-xs px-3 py-1.5 rounded-lg hover:bg-white/5 flex items-center gap-1 disabled:opacity-50"
          data-testid={`review-reject-${entry.entry_id}`}
        >
          {busy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Ignorer
        </button>
        <button
          onClick={() => run("approve", () => onApprove(entry, changed ? account : null))}
          disabled={!!busy}
          className="bg-[#22c55e] hover:bg-[#16a34a] text-[#0b0811] font-bold text-xs px-4 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
          data-testid={`review-approve-${entry.entry_id}`}
        >
          {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} {changed ? "Corriger & valider" : "Valider"}
        </button>
      </div>
    </div>
  );
}
